import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { CommandBar } from "../Shell";
import { Icon } from "../icons";
import { CATEGORIES, ERP_ENTITIES, ErrorBox } from "../ui";
import { ProcessPicker, type ProcessValue } from "../improve/ProcessPicker";

type LinkRow = { entity: string; key: string };

export function NewIssue() {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [params] = useSearchParams();
  // Raised from the process library or the catalog: the process (and step) come with it.
  const [proc, setProc] = useState<ProcessValue>({ process: params.get("process") ?? "", step: params.get("step") ?? "" });
  const [links, setLinks] = useState<LinkRow[]>(() => {
    const [entity, ...rest] = (params.get("link") ?? "").split(":");
    return entity && rest.length ? [{ entity, key: rest.join(":") }] : [{ entity: "work_center", key: "" }];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const setLink = (i: number, patch: Partial<LinkRow>) => setLinks((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!category) { setError(new Error("Pick a category.")); return; }
    if (title.trim().length < 3) { setError(new Error("Give the issue a short title.")); return; }
    setSaving(true); setError(null);
    try {
      const created = await api.createIssue({
        title: title.trim(), category, description: description.trim() || undefined,
        erpLinks: links.filter((l) => l.key.trim()).map((l) => ({ entity: l.entity, displayKey: l.key.trim().toUpperCase() })),
        ...(proc.process ? { processId: proc.process, ...(proc.step ? { step: proc.step } : {}) } : {}),
      });
      nav(`/r/${created.number}`);
    } catch (err) { setError(err); setSaving(false); }
  }

  return (
    <>
      <CommandBar status="New issue">
        <button className="cmd primary" disabled={saving} onClick={() => submit()}><Icon name="plus" size={14} />{saving ? "Saving…" : "Save"}</button>
        <button className="cmd" onClick={() => nav(-1)}>Discard</button>
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>Raise an issue</h1><span className="sub">Keep it short. A supervisor will triage it.</span></div>
        <form onSubmit={submit} className="card form-card">
          <div className="card-body">
            <ErrorBox error={error} />
            <div className="field">
              <span className="label" id="cat">Category</span>
              <div className="choice-grid" role="group" aria-labelledby="cat">
                {CATEGORIES.map((c) => (
                  <button type="button" key={c.value} className="choice" aria-pressed={category === c.value} onClick={() => setCategory(c.value)}>
                    <span style={{ fontWeight: 600 }}>{c.label}</span><small>{c.hint}</small>
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label htmlFor="title">Title</label>
              <input id="title" className="input" maxLength={200} placeholder="e.g. Burr on bracket edge" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <ProcessPicker value={proc} onChange={setProc} idPrefix="ni" hint="Not sure? Leave it: the supervisor sets it when they triage the issue." />
            <div className="field">
              <label htmlFor="desc">Details</label>
              <textarea id="desc" className="textarea" placeholder="What did you see? How many parts? What did you do about it?" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="field">
              <span className="label">Business Central records</span>
              <span className="hint">Link the work center, order, item, lot or vendor. These drive Pareto, search and traceability.</span>
              <div style={{ marginTop: 6 }}>
                {links.map((l, i) => (
                  <div className="link-row" key={i}>
                    <select className="select" aria-label="Record type" value={l.entity} onChange={(e) => setLink(i, { entity: e.target.value })}>
                      {ERP_ENTITIES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                    </select>
                    <input className="input mono" aria-label="Record number" placeholder={ERP_ENTITIES.find((x) => x.value === l.entity)?.placeholder} value={l.key} onChange={(e) => setLink(i, { key: e.target.value })} />
                    <button type="button" className="icon-btn" aria-label="Remove link" onClick={() => setLinks((ls) => ls.filter((_, j) => j !== i))}><Icon name="close" size={12} /></button>
                  </div>
                ))}
                <button type="button" className="btn" onClick={() => setLinks((ls) => [...ls, { entity: "lot", key: "" }])}><Icon name="plus" size={14} />Add a link</button>
              </div>
            </div>
            <div className="btn-row" style={{ marginTop: 8 }}>
              <button type="submit" className="btn primary" disabled={saving}>{saving ? "Saving…" : "Raise issue"}</button>
              <button type="button" className="btn" onClick={() => nav(-1)}>Cancel</button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
